<template>
  <div class="card mb-25 border-0 rounded-0 bg-white add-user-card">
    <div class="card-body p-15 letter-spacing">
      <h4 class="mb-3">Modifier assignations - {{ organeNom || "" }}</h4>

      <div class="row g-3 mb-3">
        <div class="col-md-6">
          <label class="form-label">Organes</label>
          <Multiselect
            :options="organesOptions"
            v-model="selectedOrganeId"
            track-by="value"
            label="label"
            :searchable="true"
            @input="onOrganeSelect"
            :disabled="!!isEditMode" />
        </div>

        <div class="col-md-6">
          <label class="form-label">Mandature</label>
          <Multiselect
            :options="mandaturesOptions"
            v-model="selectedMandatureId"
            track-by="value"
            label="label"
            :searchable="true"
            @input="onMandatureSelect" />
        </div>
      </div>

      <div v-if="postes.length">
        <h5>Postes</h5>
        <div class="row g-3">
          <div class="col-md-6" v-for="poste in postes" :key="poste.id">
            <div class="card p-3">
              <div class="d-flex justify-content-between align-items-start mb-2">
                <div>
                  <strong>{{ poste.numeroOrdre }} - {{ poste.nomPoste }}</strong>
                  <div v-if="poste.arrondissement?.libelle" class="text-muted small">
                    ({{ poste.arrondissement.libelle }})
                  </div>
                </div>

                <button class="btn btn-sm btn-warning" type="button" @click="openAddMemberModal(poste.id)">
                  Ajouter+
                </button>
              </div>

              <div class="mb-2">
                <Multiselect
                  :options="membresOptions"
                  v-model="poste.selectedMembre"
                  track-by="value"
                  label="label"
                  :searchable="true"
                  placeholder="Sélectionner un membre ou ajouter"
                />
              </div>

              <div class="small text-muted">
                <span v-if="poste.selectedMembre && poste.selectedMembre > 0">
                  Membre existant sélectionné
                </span>
                <span v-else-if="poste.selectedMembre && poste.selectedMembre < 0">
                  Nouveau membre temporaire : {{ poste.membre.nom }} {{ poste.membre.prenom }}
                </span>
                <span v-else class="text-danger">Aucun membre</span>
              </div>

              <!-- modal add member -->
              <div class="modal fade" :id="`modal_add_${poste.id}`" tabindex="-2" aria-hidden="true">
                <div class="modal-dialog modal-dialog-centered" style="max-width: 600px;">
                  <div class="modal-content">
                    <div class="modal-header justify-content-center text-center border-0">
                      <h5 class="modal-title">Ajouter un membre</h5>
                      <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
                    </div>
                    <div class="modal-body">
                      <div class="row justify-content-center">
                        <div class="col-md-8 mb-3">
                        <label>Nom</label>
                        <input class="form-control" v-model="poste.membre.nom" />
                      </div>
                      <div class="col-md-8 mb-3">
                        <label>Prénom</label>
                        <input class="form-control" v-model="poste.membre.prenom" />
                      </div>
                      <div class="col-md-8 mb-3">
                        <label>Image</label>
                        <input type="file" class="form-control" @change="e => onImageChange(poste, e)" />
                      </div>

                      </div>
                    </div>
                    <div class="modal-footer justify-content-center">
                      <button type="button" class="btn btn-primary" @click="confirmAddMember(poste)" data-bs-dismiss="modal">Ajouter</button>
                      <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Annuler</button>
                    </div>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>
      </div>

      <div v-else class="text-muted">Aucun poste pour cet organe</div>

      <div class="mt-4">
        <button class="btn btn-success me-2" @click="updateAssign" :disabled="loading">
          <span v-if="loading">Enregistrement…</span>
          <span v-else>Enregistrer les modifications</span>
        </button>
        <router-link to="/liste-assignation" class="btn btn-secondary">Annuler</router-link>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted } from "vue";
import Multiselect from "@vueform/multiselect";
import ApiService from "@/services/ApiService";
import { Modal } from "bootstrap";
import Swal from "sweetalert2";
import { useRoute, useRouter } from "vue-router";

export default defineComponent({
  name: "EditAssignations",
  components: { Multiselect },
  setup() {
    const route = useRoute();
    const router = useRouter();
    const organeIdParam = route.params.id ? Number(route.params.id) : null; // route param
    const queryMandature = route.query.mandatureId ? Number(route.query.mandatureId) : null;

    const organesOptions = ref<any[]>([]);
    const mandaturesOptions = ref<any[]>([]);
    const membresOptions = ref<any[]>([]);

    const selectedOrganeId = ref<number | null>(organeIdParam);
    const selectedMandatureId = ref<number | null>(queryMandature);

    const postes = ref<any[]>([]);
    const organeNom = ref<string>("");

    const loading = ref(false);
    const isEditMode = ref<boolean>(!!organeIdParam);

    onMounted(async () => {
        await fetchOrganes();
        await fetchMandatures();
        await fetchMembres();

        console.log(" On mounted — organe:", selectedOrganeId.value, "mandature:", selectedMandatureId.value);

        if (selectedOrganeId.value && selectedMandatureId.value) {
            loadAssignations(selectedOrganeId.value, selectedMandatureId.value);
        }
        });


    async function fetchOrganes() {
      const res = await ApiService.get("/organes?limit=0");
      const arr = res.data.data.data || [];
      organesOptions.value = arr.map((o: any) => ({ value: o.id, label: o.nom, postes: o.postes || [] }));
    }

    async function fetchMandatures() {
      const res = await ApiService.get("/mandature");
      const list = res.data?.data?.data || res.data?.data || res.data || [];
      mandaturesOptions.value = list.map((m: any) => ({ value: m.id, label: m.libelle }));
      // if we had a queryMandature, ensure selectedMandatureId stays
      if (queryMandature) selectedMandatureId.value = queryMandature;
    }

    async function fetchMembres() {
      const res = await ApiService.get("/membre");
      const list = res.data?.data || res.data || res;
      membresOptions.value = (Array.isArray(list) ? list : []).map((m: any) => ({ value: m.id, label: `${m.nom} ${m.prenom}` }));
    }

    function onOrganeSelect(val: number) {
  selectedOrganeId.value = val;
  organeNom.value = organesOptions.value.find(o => o.value === val)?.label || "";

  console.log("📌 Organe sélectionné :", val);

  if (selectedMandatureId.value) {
    loadAssignations(val, selectedMandatureId.value);
  }
}

    function onMandatureSelect(val: number) {
        selectedMandatureId.value = val;
        console.log("📌 Mandature sélectionnée :", val);

        if (selectedOrganeId.value && selectedMandatureId.value) {
            loadAssignations(selectedOrganeId.value, selectedMandatureId.value);
        }
        }


    async function loadAssignations(organeId: number, mandatureId: number) {
  console.log("👉 loadAssignations CALLED with :", organeId, mandatureId);

  try {
    loading.value = true;

    const res = await ApiService.get(`/organes/${organeId}/assignations`, {
      params: { mandatureId } as any
    });

    console.log("📡 URL réellement envoyée :", `/organes/${organeId}/assignations?mandatureId=${mandatureId}`);
    console.log("📥 Réponse backend :", res.data);

    const data = res.data.data || res.data;

    organeNom.value = data.nom;

    postes.value = (data.postes || []).map((p: any) => ({
      id: p.id,
      nomPoste: p.nomPoste,
      numeroOrdre: p.numeroOrdre,
      arrondissement: p.arrondissement || null,
      selectedMembre: p.membre ? p.membre.id : null,
      membre: {
        nom: p.membre?.nom || "",
        prenom: p.membre?.prenom || "",
        urlImage: null,
      },
    }));
  } catch (err: any) {
    console.error("updateAssign error:", err);

    const msg =
      err?.response?.data?.message ||
      "Erreur lors de la mise à jour des assignations";

    Swal.fire({
      icon: "warning",
      title: "Conflit détecté",
      text: msg,
    });
  }
 finally {
    loading.value = false;
  }
}


    function openAddMemberModal(posteId: number) {
      const modalEl = document.getElementById(`modal_add_${posteId}`);
      if (modalEl) {
        const modal = new Modal(modalEl);
        modal.show();
      }
    }

    function onImageChange(poste: any, event: Event) {
      const input = event.target as HTMLInputElement;
      if (!input.files || input.files.length === 0) return;
      poste.membre.urlImage = input.files[0];
    }

    function confirmAddMember(poste: any) {
      if (!poste.membre.nom || !poste.membre.prenom) {
        Swal.fire({ icon: "error", title: "Nom et prénom requis" });
        return;
      }
      // temporary negative id
      poste.selectedMembre = -poste.id;
      // add to membresOptions for display
      membresOptions.value.push({ value: -poste.id, label: `${poste.membre.nom} ${poste.membre.prenom}` });
      Swal.fire({ icon: "success", title: "Membre ajouté temporairement" });
    }

    // Build assignations and send to backend
    async function updateAssign() {
  if (!selectedOrganeId.value) {
    Swal.fire({ icon: "error", title: "Sélectionner un organe" });
    return;
  }
  if (!selectedMandatureId.value) {
    Swal.fire({ icon: "error", title: "Sélectionner une mandature" });
    return;
  }

  const assignationsPayload = postes.value.map(p => {
    if (p.selectedMembre && p.selectedMembre > 0) {
      // membre existant choisi
      return { posteId: p.id, membre: { id: p.selectedMembre } };
    } else if (p.selectedMembre && p.selectedMembre < 0) {
      // nouveau membre
      return {
        posteId: p.id,
        membre: { id: null, nom: p.membre.nom, prenom: p.membre.prenom }
      };
    } else {
      // aucun membre → suppression du poste avec mandature
      return { posteId: p.id, membre: null };
    }
  });

  const payload = {
    mandatureId: selectedMandatureId.value,
    assignations: assignationsPayload
  };

  try {
  await ApiService.put(`/organes/${selectedOrganeId.value}/assignations`, payload);
} catch (err: any) {
  if (err.response?.status === 409) {
    Swal.fire({
      icon: "error",
      title: "Conflit",
      text: err.response.data.message || "Ce membre a déjà un poste dans cet organe pour cette mandature"
    });
    return;
  }
  Swal.fire({ icon: "error", title: "Erreur serveur" });
}
 finally {
    loading.value = false;
  }
}

    return {
      organesOptions,
      mandaturesOptions,
      membresOptions,
      selectedOrganeId,
      selectedMandatureId,
      postes,
      organeNom,
      loading,
      isEditMode,
      onOrganeSelect,
      onMandatureSelect,
      openAddMemberModal,
      onImageChange,
      confirmAddMember,
      updateAssign,
    };
  },
});
</script>

<style scoped>
/* styles courts si nécessaire */
</style>
